import type { NextPage, GetServerSideProps } from 'next';
import { z } from 'zod';
import { G } from '@mobily/ts-belt';
import { Base64 } from 'js-base64';
import { serverLogger } from '../../utils/loggers/server';
import {
  LandingPageCustom,
  landingPageCustomSchema,
} from '../../components/LandingPageCustom';
import {
  ACCEPT_INVITE_COOKIE_NAME,
  isNonEmptyString,
} from '../../utils/helpers';
import { getCachedEntryByUrl } from '../../utils/requests/contentstack/cachedRequests';

type AcceptInviteSlugPageProps = z.infer<typeof landingPageCustomSchema>;

export const getServerSideProps: GetServerSideProps<
  AcceptInviteSlugPageProps
> = async ({ query, locale, req }) => {
  const slugs = Array.isArray(query.slug) ? query.slug : [query.slug ?? ''];
  const pageUrl = isNonEmptyString(slugs[0])
    ? `/accept-invite/${slugs.join('/')}`
    : `/accept-invite`;

  const language = locale;

  const entryRequest = getCachedEntryByUrl({
    contentType: 'landing_page_custom',
    url: pageUrl,
    language,
    schema: landingPageCustomSchema,
    logger: serverLogger,
  });

  let entryResponse;

  try {
    entryResponse = await entryRequest;
  } catch (error: unknown) {
    serverLogger({
      /**
       * not well-documented, but 141 is the code contentstack returns when an
       * undefined entry is requested. At time of comment, it is accompanied
       * by "The requested entry doesn't exist" text.
       */
      level: G.isObject(error) && error?.error_code === 141 ? 'warn' : 'error',
      team: 'client-onboarding',
      message: `Failed to fetch page data - /accept-invite/`,
      context: {
        error,
        page: '/accept-invite/[...slug]',
        url: pageUrl,
      },
    });
  }

  if (G.isUndefined(entryResponse)) {
    serverLogger({
      level: 'warn',
      team: 'client-onboarding',
      message: 'Required page data is undefined - /accept-invite/',
      context: {
        page: '/accept-invite/[...slug]',
        url: pageUrl,
      },
    });

    return {
      notFound: true,
    };
  }

  let modules = entryResponse.data.modules;

  // When a user clicks a referral link, Referrals-UI sets a cookie with encoded personalized credit copy
  // Ex: "Kat has given you $25 to try Stitch Fix" and redirects the user to the relevant /accept-invite route
  // If the user has that cookie we personalize the headline in the hero, if not we use a default
  const acceptInviteCookie = req.cookies[ACCEPT_INVITE_COOKIE_NAME];

  if (isNonEmptyString(acceptInviteCookie)) {
    if (modules[0].type === 'imageHalfScreenHero') {
      modules = [
        {
          type: 'imageHalfScreenHero',
          props: {
            ...modules[0].props,
            // Using Base64.decode() instead of atob() because the encoded string includes non breaking spaces which is a known issue for atob()
            title: Base64.decode(acceptInviteCookie),
          },
        },
        ...modules.slice(1),
      ];
    }
  }

  return {
    props: { seoData: entryResponse.data.seoData, modules },
  };
};

const AcceptInviteSlugPage: NextPage<AcceptInviteSlugPageProps> = ({
  seoData,
  modules,
}) => {
  return <LandingPageCustom seoData={seoData} modules={modules} />;
};

export default AcceptInviteSlugPage;
